import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { waLink } from "@/lib/format";
import { EASE } from "@/components/motion/Reveal";

const SLOTS = ["10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM", "07:00 PM"];

const empty = (vehicle) => ({ name: "", phone: "", email: "", vehicle, preferred_date: "", preferred_time: "", message: "" });

export const LeadForm = ({ type = "enquiry", vehicles = [], defaultVehicle = "", settings = {} }) => {
  const isTestDrive = type === "test_drive";
  const [form, setForm] = useState(() => empty(defaultVehicle));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => setForm((f) => ({ ...f, vehicle: defaultVehicle })), [defaultVehicle]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name.");
    if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid phone number.");
    if (isTestDrive && !form.vehicle) return toast.error("Please choose a model to drive.");
    if (isTestDrive && !form.preferred_date) return toast.error("Please pick a preferred date.");
    if (!isTestDrive && form.message.trim().length < 5) return toast.error("Tell us a little about your enquiry.");
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { ...form, type });
      setDone(data);
      toast.success(isTestDrive ? "Test drive requested. We'll confirm shortly." : "Message sent. We'll get back to you soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const vName = vehicles.find((v) => v.slug === done.vehicle)?.name || done.vehicle;
    const summary = [
      `Hello West Canal Motors, I just ${isTestDrive ? "requested a test drive" : "sent an enquiry"} on your website.`,
      `Name: ${done.name}`,
      `Phone: ${done.phone}`,
      vName && `Vehicle: ${vName}`,
      done.preferred_date && `Preferred: ${done.preferred_date}${done.preferred_time ? ` ${done.preferred_time}` : ""}`,
      done.message && `Message: ${done.message}`,
    ].filter(Boolean).join("\n");
    return (
      <motion.div data-testid="lead-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="border border-white/10 bg-surface p-8 sm:p-10">
        <CheckCircle2 className="h-10 w-10 text-crimson" />
        <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">{isTestDrive ? "Request received." : "Message sent."}</h3>
        <p className="mt-3 max-w-md text-slate-400">
          Thank you, {done.name.split(" ")[0]}. {isTestDrive ? `Our team will call ${done.phone} to confirm your ${vName ? `${vName} ` : ""}test drive${done.preferred_date ? ` on ${done.preferred_date}${done.preferred_time ? ` at ${done.preferred_time}` : ""}` : ""}.` : `We'll reach you at ${done.phone} shortly.`}
          {settings.whatsapp && " Want a faster reply? Send your details straight to our sales team on WhatsApp."}
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          {settings.whatsapp && (
            <a href={waLink(settings.whatsapp, summary)} target="_blank" rel="noreferrer" data-testid="lead-success-whatsapp" className="btn-crimson">
              Send details on WhatsApp <ArrowUpRight className="h-4 w-4" />
            </a>
          )}
          <button type="button" data-testid="lead-success-reset" onClick={() => { setDone(null); setForm(empty(defaultVehicle)); }} className="btn-ghost">Send another</button>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`lead-form-${type}`} className="border border-white/10 bg-surface p-6 sm:p-10">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor={`${type}-name`}>Full name</label>
          <input id={`${type}-name`} data-testid="lead-name-input" className="field" placeholder="Your name" value={form.name} onChange={set("name")} required />
        </div>
        <div>
          <label className="field-label" htmlFor={`${type}-phone`}>Phone / WhatsApp</label>
          <input id={`${type}-phone`} data-testid="lead-phone-input" className="field" placeholder="03XX XXXXXXX" value={form.phone} onChange={set("phone")} required />
        </div>
        <div>
          <label className="field-label" htmlFor={`${type}-email`}>Email <span className="text-slate-600">(optional)</span></label>
          <input id={`${type}-email`} type="email" data-testid="lead-email-input" className="field" placeholder="you@example.com" value={form.email} onChange={set("email")} />
        </div>
        <div>
          <label className="field-label" htmlFor={`${type}-vehicle`}>{isTestDrive ? "Model to drive" : "Interested in"}</label>
          <select id={`${type}-vehicle`} data-testid="lead-vehicle-select" className="field appearance-none" value={form.vehicle} onChange={set("vehicle")}>
            <option value="">{isTestDrive ? "Choose a model" : "Any / not sure yet"}</option>
            {vehicles.map((v) => <option key={v.slug} value={v.slug}>{v.name}</option>)}
          </select>
        </div>
        {isTestDrive && (
          <>
            <div>
              <label className="field-label" htmlFor="td-date">Preferred date</label>
              <input id="td-date" type="date" min={today} data-testid="lead-date-input" className="field" value={form.preferred_date} onChange={set("preferred_date")} />
            </div>
            <div>
              <label className="field-label" htmlFor="td-time">Preferred time</label>
              <select id="td-time" data-testid="lead-time-select" className="field appearance-none" value={form.preferred_time} onChange={set("preferred_time")}>
                <option value="">Any time</option>
                {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor={`${type}-message`}>{isTestDrive ? "Anything we should know? (optional)" : "Message"}</label>
          <textarea id={`${type}-message`} rows={4} data-testid="lead-message-input" className="field resize-none" placeholder={isTestDrive ? "e.g. I'd like to compare the T2 and T2 i-DM" : "How can we help?"} value={form.message} onChange={set("message")} />
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">We reply within business hours, usually the same day.</p>
        <button type="submit" disabled={loading} data-testid="lead-submit-button" className="btn-crimson disabled:opacity-60 disabled:hover:translate-y-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {isTestDrive ? "Request Test Drive" : "Send Message"}
          {!loading && <ArrowUpRight className="h-4 w-4" />}
        </button>
      </div>
    </form>
  );
};
